/**
 * Competence profile — what the user declares as "their domain" so Zunelo can
 * decide which signals deserve a proactive alert and which are just noise.
 *
 * Pure contracts + matcher. No provider I/O here: Slack/GitHub adapters feed
 * text/channel/repo into `matchCompetence` and persist the resulting reasons.
 */
export const COMPETENCE_LIMITS = {
    keywords: 30,
    slackChannels: 20,
    jiraProjects: 20,
    githubRepos: 30,
    keywordMinLength: 2,
    keywordMaxLength: 40,
    /** Max Slack search queries derived from one profile per sync cycle. */
    slackQueries: 8,
};
export function defaultCompetenceNotifyPreferences() {
    return {
        mentions: true,
        directMessages: true,
        threadsWaitingOnMe: true,
        competenceMatches: true,
        reviewRequests: true,
    };
}
export function defaultCompetenceProfile() {
    return {
        keywords: [],
        slackChannels: [],
        jiraProjects: [],
        githubRepos: [],
        notifyOn: defaultCompetenceNotifyPreferences(),
    };
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function stringList(value) {
    if (Array.isArray(value)) {
        return value.filter((item) => typeof item === 'string');
    }
    if (typeof value === 'string')
        return value.split(/[,\n]/);
    return [];
}
function dedupe(values, limit) {
    const seen = new Set();
    const out = [];
    for (const value of values) {
        if (!value || seen.has(value))
            continue;
        seen.add(value);
        out.push(value);
        if (out.length >= limit)
            break;
    }
    return out;
}
/** Lowercase, strip diacritics, collapse whitespace. */
export function normalizeCompetenceText(value) {
    return value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
}
function normalizeKeywords(value) {
    const cleaned = stringList(value)
        .slice(0, COMPETENCE_LIMITS.keywords * 2)
        // `:` and quotes are Slack search operators (`in:`, `from:`, phrases): a keyword must
        // never re-scope the query it is interpolated into.
        .map((raw) => normalizeCompetenceText(raw.replace(/[:"]/g, ' ')))
        .filter((kw) => kw.length >= COMPETENCE_LIMITS.keywordMinLength &&
        kw.length <= COMPETENCE_LIMITS.keywordMaxLength);
    return dedupe(cleaned, COMPETENCE_LIMITS.keywords);
}
function normalizeChannels(value) {
    const cleaned = stringList(value)
        .map((raw) => raw.trim().replace(/^#/, '').toLowerCase())
        .filter((name) => /^[a-z0-9][a-z0-9._-]{0,79}$/.test(name));
    return dedupe(cleaned, COMPETENCE_LIMITS.slackChannels);
}
function normalizeJiraProjects(value) {
    const cleaned = stringList(value)
        .map((raw) => raw.trim().toUpperCase().replace(/-\d+$/, ''))
        .filter((key) => /^[A-Z][A-Z0-9_]{1,9}$/.test(key));
    return dedupe(cleaned, COMPETENCE_LIMITS.jiraProjects);
}
function normalizeRepos(value) {
    const cleaned = stringList(value)
        .map((raw) => raw
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\/github\.com\//, '')
        .replace(/\.git$/, '')
        .replace(/\/+$/, ''))
        .filter((repo) => /^(?:[a-z0-9_.-]+\/)?[a-z0-9_.-]{1,100}$/.test(repo));
    return dedupe(cleaned, COMPETENCE_LIMITS.githubRepos);
}
function normalizeNotifyOn(value) {
    const defaults = defaultCompetenceNotifyPreferences();
    const raw = isRecord(value) ? value : {};
    const pick = (key) => typeof raw[key] === 'boolean' ? raw[key] : defaults[key];
    return {
        mentions: pick('mentions'),
        directMessages: pick('directMessages'),
        threadsWaitingOnMe: pick('threadsWaitingOnMe'),
        competenceMatches: pick('competenceMatches'),
        reviewRequests: pick('reviewRequests'),
    };
}
/** Coerce arbitrary input (Firestore doc, PUT body) into a valid profile. */
export function normalizeCompetenceProfile(input) {
    const raw = isRecord(input) ? input : {};
    return {
        keywords: normalizeKeywords(raw.keywords),
        slackChannels: normalizeChannels(raw.slackChannels),
        jiraProjects: normalizeJiraProjects(raw.jiraProjects),
        githubRepos: normalizeRepos(raw.githubRepos),
        notifyOn: normalizeNotifyOn(raw.notifyOn),
    };
}
/** True when the user declared at least one topic/channel/project/repo. */
export function hasCompetenceScope(profile) {
    return (profile.keywords.length > 0 ||
        profile.slackChannels.length > 0 ||
        profile.jiraProjects.length > 0 ||
        profile.githubRepos.length > 0);
}
function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
/** Whole-word match on normalized text; multi-word keywords match as a phrase. */
export function keywordMatches(normalizedText, keyword) {
    const pattern = new RegExp(`(^|[^\\p{L}\\p{N}_])${escapeRegExp(keyword)}(?=$|[^\\p{L}\\p{N}_])`, 'u');
    return pattern.test(normalizedText);
}
const JIRA_KEY_PATTERN = /\b([A-Z][A-Z0-9_]{1,9})-\d+\b/g;
export function extractJiraProjectKeys(text) {
    const keys = new Set();
    for (const match of text.matchAll(JIRA_KEY_PATTERN))
        keys.add(match[1]);
    return [...keys];
}
const REPO_PATTERN = /(?:github\.com\/)?\b([a-z0-9_.-]+)\/([a-z0-9_.-]+)\b/g;
function repoMatches(candidate, watched) {
    if (candidate === watched)
        return true;
    const name = candidate.includes('/') ? candidate.split('/')[1] : candidate;
    const watchedName = watched.includes('/') ? watched.split('/')[1] : watched;
    if (!watched.includes('/'))
        return name === watchedName;
    return false;
}
function reposInText(text) {
    const found = new Set();
    for (const match of text.toLowerCase().matchAll(REPO_PATTERN)) {
        found.add(`${match[1]}/${match[2].replace(/\.git$/, '')}`);
    }
    return [...found];
}
function keywordReasons(profile, text) {
    const normalized = normalizeCompetenceText(text);
    return profile.keywords
        .filter((keyword) => keywordMatches(normalized, keyword))
        .map((keyword) => ({ kind: 'keyword', value: keyword }));
}
function jiraReasons(profile, text) {
    if (!profile.jiraProjects.length)
        return [];
    const keys = extractJiraProjectKeys(text);
    return profile.jiraProjects
        .filter((project) => keys.includes(project))
        .map((project) => ({ kind: 'jira-project', value: project }));
}
function repoReasons(profile, text, repo) {
    if (!profile.githubRepos.length)
        return [];
    const candidates = [...reposInText(text), ...(repo ? [repo.toLowerCase()] : [])];
    return profile.githubRepos
        .filter((watched) => candidates.some((candidate) => repoMatches(candidate, watched)))
        .map((watched) => ({ kind: 'github-repo', value: watched }));
}
function channelReason(profile, channelName) {
    const name = channelName?.trim().replace(/^#/, '').toLowerCase();
    if (!name || !profile.slackChannels.includes(name))
        return [];
    return [{ kind: 'channel', value: name }];
}
export function matchCompetence(profile, input) {
    const text = input.text ?? '';
    const strong = [
        ...keywordReasons(profile, text),
        ...jiraReasons(profile, text),
        ...repoReasons(profile, text, input.repo),
    ];
    const weak = channelReason(profile, input.channelName);
    const reasons = [...strong, ...weak];
    if (strong.length)
        return { matched: true, strength: 'strong', reasons };
    if (weak.length)
        return { matched: true, strength: 'weak', reasons };
    return { matched: false, strength: 'none', reasons: [] };
}
const REQUEST_HINT = /\?|\b(please|pls|can you|could you|can someone|anyone|por favor|porfa|podr[ií]as?|pueden|puedes|alguien|necesito|urgente|urgent|help|ayuda|bloquea|blocked|blocker)\b/i;
/** Cheap heuristic: does this message ask something of the channel? */
export function looksLikeRequest(text) {
    return REQUEST_HINT.test(text);
}
/** Serialized form persisted on attention records (`kind:value`). */
export function serializeCompetenceReasons(reasons) {
    return reasons.map((reason) => `${reason.kind}:${reason.value}`);
}
export function parseCompetenceReasons(serialized) {
    if (!serialized)
        return [];
    const out = [];
    for (const entry of serialized) {
        const idx = entry.indexOf(':');
        if (idx <= 0)
            continue;
        const kind = entry.slice(0, idx);
        const value = entry.slice(idx + 1);
        if (!['keyword', 'channel', 'jira-project', 'github-repo'].includes(kind) || !value)
            continue;
        out.push({ kind, value });
    }
    return out;
}
/** Human label: `billing · #pagos · DEV`. */
export function describeCompetenceReasons(reasons) {
    return reasons
        .map((reason) => {
        if (reason.kind === 'channel')
            return `#${reason.value}`;
        return reason.value;
    })
        .join(' · ');
}
/**
 * Slack `search.messages` queries that surface competence hits the user is
 * not otherwise mentioned in. Keywords become quoted phrases; watched channels
 * become `in:#channel` scans. Bounded by `COMPETENCE_LIMITS.slackQueries`.
 */
export function competenceSlackQueries(profile) {
    const keywords = profile.keywords.map((keyword) => keyword.includes(' ') ? `"${keyword}"` : keyword);
    const channels = profile.slackChannels.map((channel) => `in:#${channel}`);
    // Interleave so a long keyword list can never starve the watched channels.
    const queries = [];
    const longest = Math.max(keywords.length, channels.length);
    for (let i = 0; i < longest; i += 1) {
        if (i < keywords.length)
            queries.push(keywords[i]);
        if (i < channels.length)
            queries.push(channels[i]);
    }
    return queries.slice(0, COMPETENCE_LIMITS.slackQueries);
}
//# sourceMappingURL=competence.js.map